import type { ComparisonPage } from '../types';

export const sellerboardVsWarehouse: ComparisonPage = {
  slug: 'sellerboard-vs-custom-data-warehouse',
  group: 'ecomm',
  ours: 'greyfield',
  theirs: 'sellerboard',
  title: 'Sellerboard vs a custom data warehouse: Amazon profit, or every channel?',
  metaTitle: 'Sellerboard vs a Custom Data Warehouse (2026): Honest Comparison',
  description:
    'Sellerboard is cheap, deep Amazon profit software. A warehouse puts Amazon next to Shopify, TikTok Shop, and wholesale in one model. Here is when each wins.',
  keywords: ['sellerboard alternative', 'sellerboard vs', 'sellerboard pricing', 'amazon profit analytics', 'amazon and shopify profit dashboard'],
  published: '2026-11-03',
  updated: '2026-11-03',
  tldr:
    'If Amazon is most of your business, Sellerboard is excellent value: Amazon fee detail, reimbursements, PPC automation, restock planning, and a repricer for a few dozen dollars a month. A warehouse earns its cost when Shopify, TikTok Shop, or wholesale are big enough that you need one profit number across channels, because Sellerboard runs Shopify as a separate product.',
  differences: [
    { heading: 'An Amazon operating tool vs a reporting model', body: 'Sellerboard does things: adjusts PPC bids, plans restocks, reprices, and finds reimbursement claims. A warehouse explains what happened across the whole business.' },
    { heading: 'Separate dashboards vs one model', body: 'Sellerboard combines Amazon, Walmart, and eBay, and serves Shopify through a separate product. A warehouse puts every channel in the same tables.' },
    { heading: 'Tens of dollars vs a project', body: 'Sellerboard costs $15 to $79 a month by order volume. A warehouse is a project plus Snowflake, connector, and BI bills.' },
  ],
  sections: [
    {
      heading: 'What Sellerboard does better than anyone building from scratch',
      paragraphs: [
        'Amazon fees are hard. They arrive after the order, they come from several reports, and they change. Sellerboard pulls settlement-level fees, refunds, storage, and VAT through Amazon’s API and keeps that mapping current, which is work a custom build has to own itself. We wrote about why in [where Amazon seller fees live in the SP-API](/blog/where-do-amazon-seller-fees-live-in-the-sp-api).',
        'It also acts. PPC bid automation, an inventory planner with purchase orders, a profit-aware repricer included in the subscription, and reports of lost or under-reimbursed FBA units are things a reporting warehouse does not do.',
      ],
    },
    {
      heading: 'Where it stops',
      paragraphs: [
        'Shopify is a separate Sellerboard product with its own plans and dashboards, and its cross-marketplace summary covers Amazon, Walmart, and eBay only. TikTok Shop appears only on the Shopify app’s integration list. We found no NetSuite, ERP, or retail POS connectors.',
        'Data leaves as Excel or CSV downloads, plus a read-only connector for AI tools. We found no public API, warehouse connector, or SQL access, and custom dashboard tiles choose from existing metrics rather than your own formulas.',
        'So the moment you need [Amazon profit that changes after the sale](/blog/why-amazon-profit-changes-after-sale) reconciled against Shopify and TikTok Shop orders, or you need to [avoid counting the same order twice](/blog/avoid-double-counting-shopify-amazon-tiktok-orders) when Amazon fulfils Shopify orders, you are stitching exports together by hand.',
      ],
    },
    {
      heading: 'Running both',
      paragraphs: [
        'Many Amazon-heavy brands should keep Sellerboard for PPC, restock, and reimbursements even after building a warehouse. The warehouse takes Amazon settlement data directly and owns the cross-channel profit model; Sellerboard keeps running the Amazon operation.',
      ],
    },
  ],
  pickThem: [
    'Amazon is most of your revenue.',
    'You want PPC automation, restock planning, and repricing alongside profit.',
    'You want lost and under-reimbursed FBA units flagged for claims.',
    'Your budget is tens of dollars a month.',
  ],
  pickUs: [
    'Shopify, TikTok Shop, or wholesale are a large share of revenue next to Amazon.',
    'Finance needs one profit and loss across every channel.',
    'You need the data in a warehouse with SQL access.',
    'You need your own metric formulas and cost rules.',
  ],
  byTeam: [
    { team: 'Amazon-only FBA seller', pick: 'theirs', why: 'Deep Amazon profit, PPC, and restock at very low cost.' },
    { team: 'Agency managing several Amazon accounts', pick: 'theirs', why: 'Multiple seller accounts, user permissions, and branding.' },
    { team: 'Brand on Amazon and Shopify with similar revenue', pick: 'ours', why: 'One profit model instead of two products and manual stitching.' },
    { team: 'Brand adding TikTok Shop or wholesale', pick: 'ours', why: 'Channels Sellerboard does not cover sit in the same model.' },
    { team: 'Amazon-heavy brand with a finance team', pick: 'both', why: 'Sellerboard runs Amazon operations; the warehouse owns company profit.' },
  ],
  faq: [
    {
      question: 'Does a custom warehouse replace Sellerboard?',
      answer: 'For profit reporting across channels, yes. For running Amazon, no: we do not build PPC automation, repricing, or restock tools, so Amazon-heavy brands usually keep Sellerboard for those.',
    },
    {
      question: 'Does Sellerboard work with Shopify?',
      answer: 'Through a separate product, sellerboard.io, with its own plans and dashboards. Its cross-marketplace summary covers Amazon, Walmart, and eBay.',
    },
    {
      question: 'Can I export Sellerboard data to Snowflake or BigQuery?',
      answer: 'We found no public API or warehouse connector, only Excel and CSV report downloads and a read-only MCP connector for AI tools.',
    },
    {
      question: 'How much does Sellerboard cost?',
      answer: 'Its Amazon, Walmart, and eBay plans run from $15 to $63 a month billed annually ($19 to $79 monthly), based mainly on orders per month. Shopify is a separate subscription. There is a one-month free trial.',
    },
  ],
  related: ['lifetimely-vs-custom-data-warehouse', 'triple-whale-vs-custom-data-warehouse', 'polar-analytics-vs-custom-data-warehouse'],
};
